var React = require('react');
var { createElement: h, useState, useEffect, useRef, useImperativeHandle, forwardRef } = React;
var { request } = require('../lib/apiClient');

function CreditIndicator(props) {
  var user = props.user;
  var [creditsUsed, setCreditsUsed] = useState(user && user.creditsUsed != null ? user.creditsUsed : 0);
  var monthlyCredits = user && user.planLimits && user.planLimits.monthlyCredits != null
    ? user.planLimits.monthlyCredits : null;
  var unlimited = monthlyCredits === null;
  var pct = unlimited ? 0 : (monthlyCredits > 0 ? Math.min(Math.round((creditsUsed / monthlyCredits) * 100), 100) : 0);
  var isWarning = !unlimited && pct >= 75;
  var isDanger = !unlimited && pct >= 90;
  var color = isDanger ? '#ef4444' : isWarning ? '#f59e0b' : '#6b8e5a';

  useEffect(function() {
    var interval = setInterval(function() {
      request('/auth/me').then(function(res) {
        if (res && res.user && res.user.creditsUsed != null) {
          setCreditsUsed(res.user.creditsUsed);
        }
      }).catch(function() {});
    }, 30000);
    return function() { clearInterval(interval); };
  }, []);

  return h('div', {
    className: 'canvas-footer-credit',
    title: unlimited
      ? 'AI-kreditter: ' + creditsUsed.toFixed(1) + ' brugt (ubegrænset)'
      : 'AI-kreditter: ' + creditsUsed.toFixed(1) + ' / ' + monthlyCredits + ' (' + pct + '%)'
  },
    h('svg', { width: 20, height: 20, viewBox: '0 0 36 36' },
      h('circle', { cx: 18, cy: 18, r: 15.5, fill: 'none', stroke: '#e5e7eb', strokeWidth: 3 }),
      !unlimited ? h('circle', {
        cx: 18, cy: 18, r: 15.5, fill: 'none', stroke: color, strokeWidth: 3,
        strokeDasharray: (pct * 97.4 / 100) + ' 97.4',
        strokeLinecap: 'round',
        transform: 'rotate(-90 18 18)'
      }) : null
    ),
    h('span', { className: 'canvas-footer-credit-pct', style: { color: color } },
      unlimited ? '∞' : pct + '%'
    )
  );
}

// Small circular pause icon used in the run pill. Two vertical bars when
// the workflow is running; resume (play) glyph when already paused so the
// same button toggles state.
function PauseIcon(props) {
  var paused = !!props.paused;
  if (paused) {
    return h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'currentColor', stroke: 'none' },
      h('polygon', { points: '7,4 19,12 7,20' })
    );
  }
  return h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round' },
    h('line', { x1: 9, y1: 5, x2: 9, y2: 19 }),
    h('line', { x1: 15, y1: 5, x2: 15, y2: 19 })
  );
}

var CanvasFooter = forwardRef(function CanvasFooterImpl(props, ref) {
  var historyOpen = props.historyOpen;
  var setHistoryOpen = props.setHistoryOpen;
  var showNodeDetails = props.showNodeDetails;
  var setShowNodeDetails = props.setShowNodeDetails;
  var logChatOpen = props.logChatOpen;
  var setLogChatOpen = props.setLogChatOpen;
  var workflowId = props.workflowId;
  var executionState = props.executionState;
  var publicEventStreamEnabled = props.publicEventStreamEnabled;
  var listenIndicatorTone = props.listenIndicatorTone;
  var listenIndicatorLabel = props.listenIndicatorLabel;
  var listenIndicatorDetail = props.listenIndicatorDetail;
  var historyEntries = props.historyEntries;
  var historyPos = props.historyPos;
  var onUndo = props.onUndo;
  var onRedo = props.onRedo;
  var onJumpHistory = props.onJumpHistory;
  var onRunWorkflow = props.onRunWorkflow;
  var isRunDisabled = props.isRunDisabled;
  var isExecutionRunning = props.isExecutionRunning;
  // True while the workflow auto-builder is still generating node code.
  // The run button must be locked + show a spinner during this phase so the
  // user can't fire a workflow with half-coded nodes.
  var isCodingInProgress = !!props.isCodingInProgress;
  var canvasTool = props.canvasTool;
  var setCanvasTool = props.setCanvasTool;
  var zoomLevel = props.zoomLevel;
  var changeZoom = props.changeZoom;
  var zoomRef = props.zoomRef;
  var onOpenUiPlanner = props.onOpenUiPlanner;
  var existingUiPageId = props.existingUiPageId || '';
  var onEditUiPage = props.onEditUiPage;
  var hasExistingUiPage = !!existingUiPageId;
  var viewMode = props.viewMode || 'workflow';
  var onSetViewMode = props.onSetViewMode;
  var uiWorkflowRunning = props.uiWorkflowRunning || 0;
  var onCheckSilentErrorChange = props.onCheckSilentErrorChange;
  // Controlled by the parent so the toggle reflects the *current* workflow's
  // saved value — switching workflows must show that workflow's own state, not
  // a leftover from the previous workflow.
  var checkSilentError = !!props.checkSilentError;
  var onFormatLayout = props.onFormatLayout;
  // Live mode is read-only — structural footer controls (+ UI Page, Format
  // layout, History) are disabled, matching the locked canvas.
  var readOnly = !!props.readOnly;
  var isEmbed = !!props.isEmbed;
  // Optional: when provided, a pause button appears next to the run button
  // while a workflow is running. The parent owns what "pause" means.
  var onPauseWorkflow = props.onPauseWorkflow;
  var isExecutionPaused = !!props.isExecutionPaused;
  var [editLoading, setEditLoading] = useState(false);
  // Ref on the actual play/run <button>. Exposed via the component ref so
  // callers (e.g. the post-coding QA flow) can click it programmatically —
  // same DOM element a real user click hits, so the run path is identical.
  var runBtnRef = useRef(null);
  useImperativeHandle(ref, function() {
    return {
      clickRun: function() {
        var btn = runBtnRef.current;
        if (btn && !btn.disabled) btn.click();
      }
    };
  }, []);

  function toggleCheckSilentError() {
    if (typeof onCheckSilentErrorChange === 'function') {
      onCheckSilentErrorChange(!checkSilentError);
    }
  }

  return h('div', { className: 'canvas-footer' },
    // Floating Workflow | UI segmented toggle, anchored just above the footer
    // center. Only rendered when the workflow already has a UI page (which is
    // also gated to standalone workflows in App.js — workbook-nested
    // workflows never see this).
    !isEmbed && hasExistingUiPage && onSetViewMode
      ? h('div', { className: 'canvas-footer-view-toggle' },
          h('button', {
            type: 'button',
            className: 'canvas-footer-view-toggle-btn' + (viewMode === 'workflow' ? ' canvas-footer-view-toggle-btn-active' : ''),
            title: 'Show the workflow editor',
            onClick: function(e) { e.stopPropagation(); onSetViewMode('workflow'); }
          },
            uiWorkflowRunning > 0 && viewMode === 'ui'
              ? h('span', { className: 'canvas-footer-view-toggle-spinner', 'aria-hidden': 'true' })
              : null,
            'Workflow'
          ),
          h('button', {
            type: 'button',
            className: 'canvas-footer-view-toggle-btn' + (viewMode === 'ui' ? ' canvas-footer-view-toggle-btn-active' : ''),
            title: 'Show the UI page powered by this workflow',
            onClick: function(e) { e.stopPropagation(); onSetViewMode('ui'); }
          }, 'UI')
        )
      : null,
    isEmbed ? null : h('div', { className: 'canvas-footer-left' },
      onOpenUiPlanner
        ? h('button', {
            type: 'button',
            className: 'canvas-footer-ui-page-btn',
            title: readOnly
              ? 'Disabled in Live mode (read-only)'
              : hasExistingUiPage
                ? 'Edit UI Page \u2014 open the planner to refine the UI for this workflow'
                : 'New UI Page \u2014 design a web app powered by this workflow',
            disabled: editLoading || readOnly,
            onClick: function(e) {
              e.stopPropagation();
              if (hasExistingUiPage && onEditUiPage) {
                setEditLoading(true);
                Promise.resolve(onEditUiPage(existingUiPageId))
                  .then(function() { setEditLoading(false); })
                  .catch(function() { setEditLoading(false); });
              } else {
                onOpenUiPlanner();
              }
            }
          },
            hasExistingUiPage
              // Pencil icon for "edit"
              ? h('svg', {
                  width: 14, height: 14, viewBox: '0 0 24 24',
                  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
                  strokeLinecap: 'round', strokeLinejoin: 'round'
                },
                  h('path', { d: 'M12 20h9' }),
                  h('path', { d: 'M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z' })
                )
              // Sparkle icon for "create new"
              : h('svg', {
                  width: 14, height: 14, viewBox: '0 0 24 24',
                  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
                  strokeLinecap: 'round', strokeLinejoin: 'round'
                },
                  h('path', { d: 'M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z' })
                ),
            h('span', null, editLoading ? 'Loading\u2026' : hasExistingUiPage ? 'Edit UI Page' : '+ UI Page')
          )
        : null,
      viewMode !== 'ui' ? h('button', { className: 'canvas-footer-btn canvas-footer-btn-history',
        title: readOnly ? 'Disabled in Live mode (read-only)' : 'Show changes history',
        disabled: readOnly,
        onClick: readOnly ? undefined : function() { setHistoryOpen(function(o) { return !o; }); }
      },
        h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('path', { d: 'M3 3v6h6' }),
          h('path', { d: 'M3.05 13A9 9 0 1 0 6 5.3L3 8' })
        )
      ) : null,
      // Workflow Copilot lives in the floating launcher pill (bottom-center),
      // not in the footer — the duplicate footer button was removed.
      viewMode !== 'ui' && onFormatLayout ? h('button', {
        className: 'canvas-footer-btn canvas-footer-btn-format',
        title: readOnly
          ? 'Disabled in Live mode (read-only)'
          : 'Format — click to cycle layout: Default → Tighter → Serpentine (looping)',
        disabled: readOnly,
        onClick: readOnly ? undefined : function(e) { e.stopPropagation(); onFormatLayout(); }
      },
        h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('rect', { x: 3, y: 4, width: 6, height: 6, rx: 1 }),
          h('rect', { x: 15, y: 4, width: 6, height: 6, rx: 1 }),
          h('rect', { x: 3, y: 14, width: 6, height: 6, rx: 1 }),
          h('rect', { x: 15, y: 14, width: 6, height: 6, rx: 1 }),
          h('path', { d: 'M9 7h6M9 17h6M6 10v4M18 10v4' })
        )
      ) : null,
      null,
      // Workflow Doctor toggle moved to the Header toolbar (top-right, next
      // to Widget) — see Header.js. Footer keeps the runtime-log chat button
      // above; the doctor lives in the header so it's discoverable without
      // hunting through the dim footer cluster.
      props.user ? h(CreditIndicator, { user: props.user }) : null,
      publicEventStreamEnabled
        ? h('div', {
            className: 'canvas-listen-indicator canvas-listen-indicator-' + listenIndicatorTone,
            title: listenIndicatorDetail
          },
            h('span', { className: 'canvas-listen-indicator-dot', 'aria-hidden': 'true' }),
            h('span', { className: 'canvas-listen-indicator-text' }, listenIndicatorLabel)
          )
        : null,
      historyOpen && historyEntries.length > 0
        ? h('div', { className: 'canvas-history-popup' },
            h('div', { className: 'canvas-history-header' },
              h('span', { className: 'canvas-history-title' }, 'Changes History'),
              h('div', { className: 'canvas-history-actions' },
                h('button', { className: 'canvas-history-nav-btn', onClick: onUndo, disabled: historyPos >= historyEntries.length - 1, title: 'Undo' },
                  h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2 },
                    h('path', { d: 'M1 4v6h6' }),
                    h('path', { d: 'M3.51 15a9 9 0 102.13-9.36L1 10' })
                  )
                ),
                h('button', { className: 'canvas-history-nav-btn', onClick: onRedo, disabled: historyPos <= 0, title: 'Redo' },
                  h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2 },
                    h('path', { d: 'M23 4v6h-6' }),
                    h('path', { d: 'M20.49 15a9 9 0 11-2.12-9.36L23 10' })
                  )
                )
              )
            ),
            h('div', { className: 'canvas-history-list' },
              historyEntries.map(function(entry, idx) {
                var isCurrent = idx === historyPos;
                var mins = Math.round((Date.now() - entry.time) / 60000);
                var timeLabel = mins < 1 ? 'just now' : mins === 1 ? '1 min ago' : mins + ' mins ago';
                return h('button', {
                  key: entry.id,
                  className: 'canvas-history-entry' + (isCurrent ? ' canvas-history-entry-current' : ''),
                  onClick: function() { if (onJumpHistory) onJumpHistory(idx); }
                },
                  h('span', { className: 'canvas-history-entry-label' }, entry.label),
                  h('span', { className: 'canvas-history-entry-time' }, timeLabel)
                );
              })
            )
          )
        : null
    ),
    onRunWorkflow && viewMode !== 'ui'
      ? h('div', { className: 'canvas-footer-center' },
          // Tool toggle (hand/pointer) is disabled in Live mode \u2014 the canvas is
          // read-only, so panning is the only motion and node-selection is moot.
          h('div', { className: 'canvas-footer-tool-toggle' },
            h('button', {
              className: 'canvas-footer-tool-btn' + (canvasTool === 'hand' ? ' canvas-footer-tool-btn-active' : ''),
              onClick: (readOnly && !isEmbed) ? undefined : function() { setCanvasTool('hand'); },
              disabled: readOnly && !isEmbed,
              title: isEmbed ? 'Hand tool \u2014 drag to pan' : readOnly ? 'Disabled in Live mode (read-only)' : 'Hand tool \u2014 drag to pan'
            },
              h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' },
                h('path', { d: 'M18 11V6a2 2 0 0 0-4 0' }),
                h('path', { d: 'M14 10V4a2 2 0 0 0-4 0v2' }),
                h('path', { d: 'M10 10.5V6a2 2 0 0 0-4 0v8' }),
                h('path', { d: 'M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15' })
              )
            ),
            isEmbed ? null : h('button', {
              className: 'canvas-footer-tool-btn canvas-footer-tool-btn-pointer' + (canvasTool === 'pointer' ? ' canvas-footer-tool-btn-active' : ''),
              onClick: readOnly ? undefined : function() { setCanvasTool('pointer'); },
              disabled: readOnly,
              title: readOnly ? 'Disabled in Live mode (read-only)' : 'Pointer tool \u2014 drag to select nodes'
            },
              h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'currentColor', stroke: 'none' },
                h('path', { d: 'M4 0L4 20L9.3 14.4L14 23L17.5 21.3L12.8 12.5L20 12.5Z' })
              )
            )
          ),
          h('div', { className: 'canvas-footer-pill' },
            !isEmbed && onUndo ? h('button', {
              className: 'canvas-footer-pill-btn',
              onClick: readOnly ? undefined : onUndo,
              disabled: readOnly || historyPos >= historyEntries.length - 1,
              title: readOnly ? 'Disabled in Live mode (read-only)' : 'Undo'
            },
              h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                h('path', { d: 'M1 4v6h6' }),
                h('path', { d: 'M3.51 15a9 9 0 102.13-9.36L1 10' })
              )
            ) : null,
            !isEmbed && onPauseWorkflow && (isExecutionRunning || isExecutionPaused) ? h('button', {
              className: 'canvas-footer-pill-btn' + (isExecutionPaused ? ' canvas-footer-pill-btn-active' : ''),
              onClick: function(e) { e.stopPropagation(); onPauseWorkflow(); },
              title: isExecutionPaused ? 'Resume workflow' : 'Pause workflow',
              'aria-pressed': isExecutionPaused ? 'true' : 'false'
            },
              h(PauseIcon, { paused: isExecutionPaused })
            ) : null,
            h('button', {
              ref: runBtnRef,
              className: 'canvas-footer-run-btn'
                + (isExecutionRunning ? ' canvas-footer-run-btn-stop' : '')
                + ((isRunDisabled || isCodingInProgress) ? ' canvas-footer-run-btn-disabled' : '')
                + (isCodingInProgress ? ' canvas-footer-run-btn-coding' : ''),
              onClick: onRunWorkflow,
              title: isCodingInProgress
                ? 'Generating node code… hold on, the workflow will run automatically when coding finishes.'
                : isRunDisabled
                  ? 'Turn off Listen mode to run the workflow from the builder.'
                  : (isExecutionRunning ? 'Stop workflow' : isExecutionPaused ? 'Resume workflow' : 'Run workflow'),
              disabled: isRunDisabled || isCodingInProgress
            },
              isCodingInProgress
                ? h('span', { className: 'canvas-footer-run-btn-spinner', 'aria-hidden': 'true' })
                : isExecutionRunning
                  ? h('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                      h('rect', { x: 6, y: 6, width: 12, height: 12, rx: 2 })
                    )
                  : h('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'currentColor', stroke: 'none' },
                      h('polygon', { points: '6,3 20,12 6,21' })
                    )
            ),
            !isEmbed && onRedo ? h('button', {
              className: 'canvas-footer-pill-btn',
              onClick: readOnly ? undefined : onRedo,
              disabled: readOnly || historyPos <= 0,
              title: readOnly ? 'Disabled in Live mode (read-only)' : 'Redo'
            },
              h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                h('path', { d: 'M23 4v6h-6' }),
                h('path', { d: 'M20.49 15a9 9 0 11-2.12-9.36L23 10' })
              )
            ) : null,
            // Check Silent Error toggle is HIDDEN in Live mode \u2014 Live runs the
            // published snapshot and force the checker OFF (see App.js run path),
            // so there's nothing to toggle here.
            readOnly ? null : h('button', {
              className: 'canvas-footer-pill-btn' + (checkSilentError ? ' canvas-footer-pill-btn-active' : ''),
              onClick: toggleCheckSilentError,
              title: 'Check Silent Error \u2014 ' + (checkSilentError ? 'ON' : 'OFF'),
              'aria-pressed': checkSilentError ? 'true' : 'false'
            },
              h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                h('rect', { x: 8, y: 6, width: 8, height: 14, rx: 4 }),
                h('path', { d: 'M12 6V3' }),
                h('path', { d: 'M10 3h4' }),
                h('path', { d: 'M8 10H4' }),
                h('path', { d: 'M20 10h-4' }),
                h('path', { d: 'M8 14H3' }),
                h('path', { d: 'M21 14h-5' }),
                h('path', { d: 'M8 18H5' }),
                h('path', { d: 'M19 18h-3' })
              )
            )
          )
        )
      : null,
    viewMode !== 'ui' ? h('div', { className: 'canvas-footer-right' },
      h('button', {
        className: 'canvas-zoom-btn',
        onClick: function() { changeZoom(zoomRef.current - 0.1); },
        disabled: zoomLevel <= 0.25
      }, '\u2212'),
      h('button', {
        className: 'canvas-zoom-level',
        onClick: function() { changeZoom(1); },
        title: 'Reset zoom'
      }, Math.round(zoomLevel * 100) + '%'),
      h('button', {
        className: 'canvas-zoom-btn',
        onClick: function() { changeZoom(zoomRef.current + 0.1); },
        disabled: zoomLevel >= 1
      }, '+')
    ) : null
  );
});

module.exports = CanvasFooter;
